import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { UserRank, ClanRank, RefereeRank, CreateUserRankRequest, UpdateUserRankRequest, CreateClanRankRequest, UpdateClanRankRequest, CreateRefereeRankRequest, UpdateRefereeRankRequest } from '../../services/adminRanksService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: rgba(26, 35, 46, 0.95);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  color: #00d4ff;
  margin: 0;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #00d4ff;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Input = styled.input`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.15);
  }

  &::placeholder {
    color: #666;
  }
`;

const Select = styled.select`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.15);
  }

  option {
    background: #1a232e;
    color: #fff;
  }
`;

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CurrentImage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ImagePreview = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  background: rgba(0, 212, 255, 0.05);
`;

const FileInput = styled.input`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.15);
  }

  &::file-selector-button {
    background: #00d4ff;
    color: #000;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    margin-right: 10px;
  }
`;

const UploadButton = styled.button`
  background: #ff9800;
  color: #000;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.3s ease;
  align-self: flex-start;

  &:hover {
    background: #e68900;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 10px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  background: ${props => props.$variant === 'secondary' ? 'transparent' : '#00d4ff'};
  color: ${props => props.$variant === 'secondary' ? '#00d4ff' : '#000'};
  border: ${props => props.$variant === 'secondary' ? '1px solid #00d4ff' : 'none'};
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$variant === 'secondary' ? 'rgba(0, 212, 255, 0.1)' : '#00b8e6'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  font-size: 12px;
  margin-top: 5px;
`;

type RankType = 'user' | 'clan' | 'referee';
type RankData = UserRank | ClanRank | RefereeRank;

interface RankFormModalProps {
  rank?: RankData | null;
  rankType: RankType;
  onSave: (data: any, imageFile?: File) => Promise<void>;
  onClose: () => void;
  initialFaction?: string; // For pre-selecting faction when creating
}

const RankFormModal: React.FC<RankFormModalProps> = ({
  rank,
  rankType,
  onSave,
  onClose,
  initialFaction
}) => {
  const [formData, setFormData] = useState({
    faction: (rank && 'faction' in rank) ? rank.faction : (initialFaction || 'human'),
    level: rank?.level || 0,
    name: rank?.name || '',
    pointsRequired: rank?.pointsRequired || 0,
    iconUrl: rank?.iconUrl || ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const isEditing = !!rank;

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (formData.level < 0 || formData.level > 49) {
      setError('Level must be between 0 and 49');
      return;
    }
    if (formData.pointsRequired < 0) {
      setError('Points required must be non-negative');
      return;
    }
    if (rankType === 'user' && !['human', 'alien'].includes(formData.faction)) {
      setError('Faction must be human or alien');
      return;
    }

    try {
      setSaving(true);
      const data = rankType === 'user' 
        ? formData 
        : { ...formData, faction: undefined };
      
      await onSave(data, selectedFile || undefined);
    } catch (error: any) {
      console.error('Error saving rank:', error);
      setError(error.response?.data?.message || error.message || 'Error saving rank');
    } finally {
      setSaving(false);
    }
  };

  const getModalTitle = () => {
    const action = isEditing ? 'Edit' : 'Create';
    const type = rankType.charAt(0).toUpperCase() + rankType.slice(1);
    return `${action} ${type} Rank`;
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{getModalTitle()}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {rankType === 'user' && (
            <FormGroup>
              <Label>Faction</Label>
              <Select
                value={formData.faction}
                onChange={(e) => handleInputChange('faction', e.target.value)}
                required
              >
                <option value="human">Human</option>
                <option value="alien">Alien</option>
              </Select>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Level</Label>
            <Input
              type="number"
              min="0"
              max="49"
              value={formData.level}
              onChange={(e) => handleInputChange('level', parseInt(e.target.value) || 0)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter rank name"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Points Required</Label>
            <Input
              type="number"
              min="0"
              value={formData.pointsRequired}
              onChange={(e) => handleInputChange('pointsRequired', parseInt(e.target.value) || 0)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Icon</Label>
            <ImageUploadContainer>
              {formData.iconUrl && !selectedFile && (
                <CurrentImage>
                  <ImagePreview src={formData.iconUrl} alt="Current icon" />
                  <span style={{ color: '#ccc', fontSize: '14px' }}>Current icon</span>
                </CurrentImage>
              )}
              {selectedFile && (
                <CurrentImage>
                  <span style={{ color: '#00d4ff', fontSize: '14px' }}>
                    New image selected: {selectedFile.name}
                  </span>
                </CurrentImage>
              )}
              <FileInput
                id="imageFile"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </ImageUploadContainer>
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonContainer>
            <Button type="button" $variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
            </Button>
          </ButtonContainer>
        </Form>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default RankFormModal;